// PRD 3.1~3.3: TUI 入口 —— 串联 SSE 解析 + Markdown 渲染 + 对话流组件
import './styles/main.css';
import { parseSSE } from './core/sse-client.js';
import { ChatStream } from './components/chat-stream.js';
import { ActionChips } from './components/action-chips.js';
import { loadSuggestions } from './config/suggestions.js';

const API_BASE = '/api'; // 由 vite.config.js 代理至 BFF

const $app = document.getElementById('app');
const $welcome = document.getElementById('welcome');
const $chatStream = document.getElementById('chat-stream');
const $chatBottom = document.getElementById('chat-bottom');
const $inputArea = document.getElementById('prompt-input');
const $sendBtn = document.getElementById('send-btn');
const $actionChips = document.getElementById('action-chips');
const $inputAreaWrapper = document.getElementById('input-area');
const $chatTopbar = document.getElementById('chat-topbar');
const $topbarHome = document.getElementById('topbar-home');
const $welcomeNav = document.querySelector('.welcome__nav');

const chat = new ChatStream($chatStream);
const chips = new ActionChips($actionChips);

// 对话历史，每次请求带上以保持多轮上下文
const history = [];

// 视图状态：welcome（首屏中央） / chat（对话流布局）
let isChatMode = false;

// 当前 SSE 请求的 AbortController；home 时用于中断流
let currentAbort = null;

/** 从欢迎页切换到聊天模式：迁移 chips 与 input 到底部容器 */
function switchToChatMode() {
  if (isChatMode) return;
  isChatMode = true;

  $welcome.hidden = true;
  $app.classList.remove('tui-app--welcome');
  $app.classList.add('tui-app--chat');

  // 显示顶部栏、chat-stream 与底部输入区
  $chatTopbar.hidden = false;
  $chatStream.hidden = false;
  $chatBottom.hidden = false;

  // 把 chips 与 input 迁移到 chat-bottom（事件监听保留）
  $chatBottom.appendChild($actionChips);
  $chatBottom.appendChild($inputAreaWrapper);

  $chatStream.scrollTop = $chatStream.scrollHeight;
}

/** 从聊天模式返回欢迎页：中断流 + 清空对话 + 迁回 chips/input */
function switchToWelcomeMode() {
  if (!isChatMode) return;
  isChatMode = false;

  // 中断进行中的 SSE 流（fetch 抛 AbortError，send() 的 catch 静默处理）
  if (currentAbort) {
    currentAbort.abort();
    currentAbort = null;
  }

  // 清空对话历史与对话流 DOM
  history.length = 0;
  $chatStream.replaceChildren();

  // 清空输入框草稿
  $inputArea.value = '';
  $inputArea.style.height = 'auto';
  $sendBtn.disabled = false;

  // 隐藏 chat 视图，恢复 welcome
  $chatTopbar.hidden = true;
  $chatStream.hidden = true;
  $chatBottom.hidden = true;
  $welcome.hidden = false;
  $app.classList.remove('tui-app--chat');
  $app.classList.add('tui-app--welcome');

  // chips 与 input 迁回 welcome（插到 nav 之前，保持原 DOM 顺序：logo → chips → input → nav）
  $welcome.insertBefore($actionChips, $welcomeNav);
  $welcome.insertBefore($inputAreaWrapper, $welcomeNav);

  // 恢复推荐问题显示（send() 里 hide 过）
  chips.show();
}

// PRD 3.2: 推荐问题 —— 初始展示，点击后填入输入框并自动发送
loadSuggestions().then((list) => chips.render(list));
chips.onSelectCb((text) => {
  $inputArea.value = text;
  send(text);
});

// Follow-up：消息内联推荐问题，点击后填入输入框并自动发送
chat.onFollowUpSelectCb((text) => {
  $inputArea.value = text;
  send(text);
});

async function send(text) {
  if (!text.trim()) return;
  switchToChatMode();
  chips.hide(); // 开始对话后隐藏推荐位
  chat.appendMessage('user', text);
  $inputArea.value = '';
  $inputArea.style.height = 'auto';
  $sendBtn.disabled = true;

  const stream = chat.createStreamingMessage('assistant');
  stream.thinking();
  let assistantText = '';

  const abort = new AbortController();
  currentAbort = abort;

  try {
    const res = await fetch(`${API_BASE}/chat`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ message: text, history }),
      signal: abort.signal,
    });

    if (!res.ok || !res.body) {
      const errText = await res.text().catch(() => res.statusText);
      throw new Error(`HTTP ${res.status}: ${errText}`);
    }

    // PRD 3.1: 流式打字机效果 —— 逐 delta 追加渲染
    for await (const { event, data } of parseSSE(res)) {
      if (event === 'delta') {
        try {
          const { text: delta } = JSON.parse(data);
          if (delta) {
            assistantText += delta;
            stream.append(delta);
          }
        } catch {
          /* 忽略非 JSON delta */
        }
      } else if (event === 'suggestions') {
        // follow-up 推荐问题：后端从 LLM 输出剥离的 <<FOLLOW>>...<<END>> 标记
        try {
          const { items } = JSON.parse(data);
          if (Array.isArray(items) && items.length > 0) {
            stream.appendFollowUp(items);
          }
        } catch {
          /* 忽略非法 suggestions */
        }
      } else if (event === 'error') {
        try {
          const { message } = JSON.parse(data);
          stream.error(message);
          return;
        } catch {
          stream.error(data);
          return;
        }
      }
      // event === 'done' 自然结束循环
    }
    stream.done();
    // 本轮完成后推入历史，供下一轮携带上下文
    history.push({ role: 'user', content: text });
    history.push({ role: 'assistant', content: assistantText });
  } catch (e) {
    // 被 home 中断：不显示错误，DOM 已由 switchToWelcomeMode 清空
    if (abort.signal.aborted) return;
    stream.error(e.message);
  } finally {
    if (currentAbort === abort) currentAbort = null;
    $sendBtn.disabled = false;
    if (isChatMode) $inputArea.focus();
  }
}

// PRD 3.1: 多行自动撑开
$inputArea.addEventListener('input', () => {
  $inputArea.style.height = 'auto';
  $inputArea.style.height = Math.min($inputArea.scrollHeight, 200) + 'px';
});

$inputArea.addEventListener('keydown', (e) => {
  if (e.key === 'Enter' && !e.shiftKey) {
    e.preventDefault();
    send($inputArea.value);
  }
});

$sendBtn.addEventListener('click', () => send($inputArea.value));

// ============ 顶部栏 ============
// home 按钮：返回首页，开始新对话（中断流 + 清空）
$topbarHome.addEventListener('click', () => {
  switchToWelcomeMode();
});
